import _ from 'lodash';
import { useLayoutEffect, useMemo, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type MeasureRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  left: number;
  bottom: number;
  right: number;
};

type UseMeasureResult<T extends Element> = [(element: T | null) => void, Partial<MeasureRect>];

export default function useMeasure<T extends Element = HTMLElement>(): UseMeasureResult<T> {
  const [element, ref] = useState<T | null>(null);
  const [rect, setRect] = useState<Partial<MeasureRect>>({});
  const observer = useMemo(
    () =>
      new ResizeObserver((entries) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }
          if (entries[0]) {
            const { contentRect } = entries[0];
            const newRect: MeasureRect = {
              x: contentRect.x,
              y: contentRect.y,
              width: contentRect.width,
              height: contentRect.height,
              top: contentRect.top,
              left: contentRect.left,
              bottom: contentRect.bottom,
              right: contentRect.right,
            };
            if (!_.isEqual(rect, newRect)) {
              setRect(newRect);
            }
          }
        });
      }),
    [rect],
  );
  useLayoutEffect(() => {
    if (!element) return;
    // ResizeObserver's first callback fires asynchronously (queued in a requestAnimationFrame),
    // so consumers that size themselves off `rect.width`/`rect.height` would otherwise render at
    // an unmeasured size for a frame or two, then visibly jump once the observer catches up.
    // Reading the rect synchronously here, before paint, avoids that flash.
    const { x, y, width, height, top, left, bottom, right } = element.getBoundingClientRect();
    setRect((current) =>
      _.isEqual(current, { x, y, width, height, top, left, bottom, right })
        ? current
        : { x, y, width, height, top, left, bottom, right },
    );
    observer.observe(element);
    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element]);

  return [ref, rect];
}
